import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { flowRight } from 'lodash'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router-dom'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import PropTypes from 'prop-types'
import { getFinancingApplicationEditPath } from 'lib/utils/store/financingApplication'
import { getLocale } from 'lib/utils/getLocale'

import Button from 'components/UI/Button'
import Link from 'components/Link'
import StatusMessage from 'components/StatusMessage'
import ErrorComponent from 'components/Errors/ErrorComponent'
import { DataLoading } from 'components/Util/Loading'

import './financing-application-edit.pcss'

import getFinancingApplication from 'graphql/financing/getFinancingApplication.gql'
import cancelFinancingApplicationMutation from 'graphql/financing/cancelFinancingApplication.gql'

const FinancingApplicationEdit = ({ history, financingApplicationId }) => {
    const { t, i18n } = useTranslation(['financing'])

    const query = useQuery(getFinancingApplication, { variables: { id: financingApplicationId } })

    const financingApplication = query.data && query.data.financingApplication

    const [cancelFinancingApplication, { loading: cancelLoading, error: cancelError }] = useMutation(cancelFinancingApplicationMutation, {
        variables: { id: financingApplicationId }
    })
    const [cancelConfirmation, setCancelConfirmation] = useState(false)

    const cancelApplication = () => {
        cancelFinancingApplication()
            .then(() => history.push(`${getLocale(i18n.language)}/financing/application/${financingApplication.id}`))
    }

    return (
        <DataLoading data={query}>
            {() => (
                <div className="financing-application-edit">
                    <div className="financing-application-edit__section">
                        <div className="financing-application-edit__section-title">
                            {t('financing:application.editModal.editFinance.title')}
                        </div>
                        <div className="financing-application-edit__section-line">
                            {t('financing:application.editModal.editFinance.message')}
                        </div>
                        <div className="financing-application-edit__actions">
                            <Link
                                to={`${getLocale(i18n.language)}${getFinancingApplicationEditPath(financingApplication.id)}`}
                                trackName="editFinance"
                                className="button button--secondary"
                            >
                                {t('financing:application.editModal.editFinance.action')}
                            </Link>
                        </div>
                    </div>
                    <div className="financing-application-edit__section">
                        <div className="financing-application-edit__section-title">
                            {t('financing:application.editModal.newFinance.title')}
                        </div>
                        <div className="financing-application-edit__section-line">
                            {t('financing:application.editModal.newFinance.message')}
                        </div>
                        <div className="financing-application-edit__actions">
                            <Link
                                to={`${getLocale(i18n.language)}/autorahoitus`}
                                trackName="newFinance"
                                className="button button--secondary"
                            >
                                {t('financing:application.editModal.newFinance.action')}
                            </Link>
                        </div>
                    </div>
                    <div className="financing-application-edit__section">
                        <div className="financing-application-edit__section-title">
                            {t('financing:application.editModal.cancelFinance.title')}
                        </div>
                        <div className="financing-application-edit__section-line">
                            {t('financing:application.editModal.cancelFinance.message')}
                        </div>
                        {!cancelConfirmation ? (
                            <div className="financing-application-edit__actions">
                                <Button
                                    disabled={financingApplication.hasLinkedBos}
                                    trackName="cancelFinance"
                                    className="button button--secondary"
                                    onClick={() => setCancelConfirmation(true)}
                                >
                                    {t('financing:application.editModal.cancelFinance.action')}
                                </Button>
                            </div>
                        ) : (
                            <>
                                {cancelError && <ErrorComponent error={cancelError} container={false} />}
                                <StatusMessage
                                    type="alert"
                                    cssClass={cancelLoading ? 'loading' : ''}
                                    appContext="cancelApplicationConfirmation"
                                >
                                    <div>
                                        <p>{t('financing:application.editModal.cancelFinance.confirmation.message')}</p>
                                        <div className="action-wrapper">
                                            <Button
                                                trackName="cancelApplication"
                                                className="button button--delete"
                                                onClick={cancelApplication}
                                            >
                                                {t('financing:application.editModal.cancelFinance.confirmation.accept')}
                                            </Button>
                                            <Button
                                                trackName="abort"
                                                className="button button--secondary"
                                                onClick={() => setCancelConfirmation(false)}
                                            >
                                                {t('financing:application.editModal.cancelFinance.confirmation.abort')}
                                            </Button>
                                        </div>
                                    </div>
                                </StatusMessage>
                            </>
                        )}
                    </div>
                </div>
            )}
        </DataLoading>
    )
}

FinancingApplicationEdit.propTypes = {
    financingApplicationId: PropTypes.string,
}

export default flowRight(
    withErrorBoundaryNoRender,
    withRouter,
)(FinancingApplicationEdit)
